// ─── React & External Libraries ──────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { offset } from "@floating-ui/react"
import { DragHandle } from "@tiptap/extension-drag-handle-react"

// ─── Hooks ────────────────────────────────────────────────────────────────────
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"
import { useUiEditorState } from "@/tiptap/hooks/use-ui-editor-state"
import { selectNodeAndHideFloating } from "@/tiptap/hooks/use-floating-toolbar-visibility"

// ─── Primitive UI Components ──────────────────────────────────────────────────
import { Button } from "@/tiptap/ui-primitive/button"
import { Spacer } from "@/tiptap/ui-primitive/spacer"
import { Separator } from "@/tiptap/ui-primitive/separator"
import {
  Card,
  CardBody,
  CardGroupLabel,
  CardItemGroup,
} from "@/tiptap/ui-primitive/card"
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuGroup,
  MenuGroupLabel,
  MenuButton,
} from "@/tiptap/ui-primitive/menu"
import { Combobox, ComboboxList } from "@/tiptap/ui-primitive/combobox"

// ─── Tiptap UI — Hooks ────────────────────────────────────────────────────────
import { useImageDownload } from "@/tiptap/ui/image-download-button"
import {
  useDuplicate,
  DuplicateShortcutBadge,
} from "@/tiptap/ui/duplicate-button"
import {
  useCopyToClipboard,
  CopyToClipboardShortcutBadge,
} from "@/tiptap/ui/copy-to-clipboard-button"
import {
  useDeleteNode,
  DeleteNodeShortcutBadge,
} from "@/tiptap/ui/delete-node-button"
import {
  useCopyAnchorLink,
  CopyAnchorLinkShortcutBadge,
} from "@/tiptap/ui/copy-anchor-link-button"
import { useResetAllFormatting } from "@/tiptap/ui/reset-all-formatting-button"
import {
  useAiAsk,
  AskAiShortcutBadge,
} from "@/tiptap/ui/ai-ask-button"
import { useText } from "@/tiptap/ui/text-button"
import { useHeading } from "@/tiptap/ui/heading-button"
import { useList } from "@/tiptap/ui/list-button"
import { useBlockquote } from "@/tiptap/ui/blockquote-button"
import { useCodeBlock } from "@/tiptap/ui/code-block-button"
import { buildCalloutAction } from "@/tiptap/ui/callout-menu/callout-menu"
import { MediaForm } from "@/tiptap/ui/media-form/media-form"
import { turnActiveCalloutListIntoList } from "@/tiptap/ui/turn-into-dropdown/callout-list-transform"
import {
  getMediaFormValues,
  isMediaNodeType,
  updateMediaNode,
} from "@/tiptap/ui/slash-dropdown-menu/use-slash-dropdown-menu"

// ─── Tiptap UI — Components ───────────────────────────────────────────────────
import { SlashCommandTriggerButton } from "@/tiptap/ui/slash-command-trigger-button"
import { ColorMenu } from "@/tiptap/ui/color-menu"

// ─── Tiptap Node Extensions ───────────────────────────────────────────────────
import { TableAlignMenu } from "@/tiptap/nodes/table-node/ui/table-alignment-menu"
import { useTableFitToWidth } from "@/tiptap/nodes/table-node/ui/table-fit-to-width-button"
import { useTableClearRowColumnContent } from "@/tiptap/nodes/table-node/ui/table-clear-row-column-content-button"
import { useTocShowTitle } from "@/tiptap/nodes/toc-node/ui/toc-show-title-button"

// ─── Utils ────────────────────────────────────────────────────────────────────
import {
  getNodeDisplayName,
  isTextSelectionValid,
} from "@/tiptap/lib/tiptap-collab-utils"
import { isNodeInSchema, SR_ONLY } from "@/tiptap/lib/tiptap-utils"
import { rootLevelOrImageDragTargets } from "@/tiptap/ui/drag-context-menu/drag-handle-targets"

// ─── Icons ────────────────────────────────────────────────────────────────────
import { GripVerticalIcon } from "@/tiptap/icons/grip-vertical-icon"
import { ChevronRightIcon } from "@/tiptap/icons/chevron-right-icon"
import { Repeat2Icon } from "@/tiptap/icons/repeat-2-icon"
import { ExternalLinkIcon } from "@/tiptap/icons/external-link-icon"

// ─── Styles ───────────────────────────────────────────────────────────────────

const DRAG_AUTO_SCROLL_EDGE_SIZE = 96
const DRAG_AUTO_SCROLL_MAX_SPEED = 28
const SCROLLABLE_OVERFLOW_VALUES = new Set(["auto", "scroll", "overlay"])

const isWindowScrollTarget = (target) => target === window

const getWindowScrollElement = () =>
  document.scrollingElement || document.documentElement

const getScrollTop = (target) =>
  isWindowScrollTarget(target) ? window.scrollY : target.scrollTop

const getMaxScrollTop = (target) => {
  if (isWindowScrollTarget(target)) {
    const scrollElement = getWindowScrollElement()
    const body = document.body

    return Math.max(
      0,
      scrollElement.scrollHeight,
      body?.scrollHeight || 0,
      scrollElement.offsetHeight,
      body?.offsetHeight || 0,
      scrollElement.clientHeight,
    ) - window.innerHeight
  }

  return Math.max(0, target.scrollHeight - target.clientHeight)
}

const getScrollTargetRect = (target) => {
  if (isWindowScrollTarget(target)) {
    return {
      top: 0,
      bottom: window.innerHeight,
    }
  }

  const rect = target.getBoundingClientRect()

  return {
    top: rect.top,
    bottom: rect.bottom,
  }
}

const getDragScrollAmount = (clientY, target) => {
  const rect = getScrollTargetRect(target)
  const distanceFromTop = clientY - rect.top
  const distanceFromBottom = rect.bottom - clientY
  let direction = 0
  let distanceFromEdge = DRAG_AUTO_SCROLL_EDGE_SIZE

  if (distanceFromTop < DRAG_AUTO_SCROLL_EDGE_SIZE) {
    direction = -1
    distanceFromEdge = distanceFromTop
  } else if (distanceFromBottom < DRAG_AUTO_SCROLL_EDGE_SIZE) {
    direction = 1
    distanceFromEdge = distanceFromBottom
  }

  if (!direction) return 0

  const intensity = Math.min(
    1,
    Math.max(0, (DRAG_AUTO_SCROLL_EDGE_SIZE - distanceFromEdge) / DRAG_AUTO_SCROLL_EDGE_SIZE),
  )
  const amount = Math.ceil(intensity * DRAG_AUTO_SCROLL_MAX_SPEED) * direction
  const scrollTop = getScrollTop(target)
  const maxScrollTop = getMaxScrollTop(target)

  if ((amount < 0 && scrollTop <= 0) || (amount > 0 && scrollTop >= maxScrollTop)) {
    return 0
  }

  return amount
}

const scrollTargetBy = (target, amount) => {
  if (!amount) return

  if (isWindowScrollTarget(target)) {
    window.scrollBy(0, amount)
    return
  }

  target.scrollTop += amount
}

const isVerticallyScrollable = (element) => {
  if (!element || element === document.body || element === document.documentElement) {
    return false
  }

  const style = window.getComputedStyle(element)

  return (
    SCROLLABLE_OVERFLOW_VALUES.has(style.overflowY) &&
    element.scrollHeight > element.clientHeight + 1
  )
}

const findVerticalScrollTarget = (element) => {
  let current = element

  while (current) {
    if (isVerticallyScrollable(current)) {
      return current
    }

    current = current.parentElement
  }

  return window
}

const isDragHandleEventTarget = (target) =>
  target instanceof Element && Boolean(target.closest(".drag-handle"))

const isImageDragHandleEventTarget = (target) =>
  target instanceof Element && Boolean(target.closest("[data-image-drag-handle]"))

const useDragAutoScroll = (editor, active) => {
  const clientYRef = useRef(null)

  useEffect(() => {
    if (!editor || !active) return

    let animationFrameId = null

    const updateClientY = (event) => {
      if (!Number.isFinite(event.clientY)) return
      if (event.type === "drag" && event.clientX === 0 && event.clientY === 0) return

      clientYRef.current = event.clientY
    }

    const tick = () => {
      const clientY = clientYRef.current
      const scrollTarget = findVerticalScrollTarget(editor.view.dom)

      if (
        Number.isFinite(clientY) &&
        (isWindowScrollTarget(scrollTarget) || scrollTarget.isConnected)
      ) {
        scrollTargetBy(scrollTarget, getDragScrollAmount(clientY, scrollTarget))
      }

      animationFrameId = window.requestAnimationFrame(tick)
    }

    document.addEventListener("drag", updateClientY, true)
    document.addEventListener("dragover", updateClientY, true)
    animationFrameId = window.requestAnimationFrame(tick)

    return () => {
      document.removeEventListener("drag", updateClientY, true)
      document.removeEventListener("dragover", updateClientY, true)

      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId)
      }

      clientYRef.current = null
    }
  }, [editor, active])
}

const useNodeTransformActions = () => {
  const { editor } = useTiptapEditor()
  const text = useText()
  const heading1 = useHeading({ level: 1 })
  const heading2 = useHeading({ level: 2 })
  const heading3 = useHeading({ level: 3 })
  const heading4 = useHeading({ level: 4 })
  const bulletList = useList({ type: "bulletList" })
  const orderedList = useList({ type: "orderedList" })
  const taskList = useList({ type: "taskList" })
  const blockquote = useBlockquote()
  const codeBlock = useCodeBlock()

  const mapper = (
    action,
    type
  ) => ({
    icon: action.Icon,
    label: action.label,
    onClick: () => {
      if (type && turnActiveCalloutListIntoList(editor, type)) {
        return true
      }

      return action.handleToggle()
    },
    disabled: !action.canToggle,
    isActive: action.isActive,
  })

  const calloutAction = editor && isNodeInSchema("callout", editor)
    ? buildCalloutAction(editor)
    : null

  const actions = [
    mapper(text),
    ...[heading1, heading2, heading3, heading4].map((action) => mapper(action)),
    mapper(bulletList, "bulletList"),
    mapper(orderedList, "orderedList"),
    mapper(taskList, "taskList"),
    mapper(blockquote),
    calloutAction,
    mapper(codeBlock),
  ].filter(Boolean)

  const allDisabled = actions.every((a) => a.disabled)

  return allDisabled ? null : actions
}

const BaseMenuItem = ({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  isActive = false,
  shortcutBadge,
  calloutVariant,
}) => (
  <MenuItem
    data-callout-variant={calloutVariant}
    render={
      <Button
        variant="ghost"
        data-callout-variant={calloutVariant}
        data-active-state={isActive ? "on" : "off"} />
    }
    onClick={onClick}
    disabled={disabled}>
    <Icon className="tiptap-button-icon" />
    <span className="tiptap-button-text">{label}</span>
    {shortcutBadge}
  </MenuItem>
)

const SubMenuTrigger = ({ icon: Icon, label, children }) => (
  <Menu
    placement="right"
    trigger={
      <MenuItem
        render={
          <MenuButton
            render={
              <Button variant="ghost">
                <Icon className="tiptap-button-icon" />
                <span className="tiptap-button-text">{label}</span>
                <Spacer />
                <ChevronRightIcon className="tiptap-button-icon" />
              </Button>
            } />
        } />
    }>
    <MenuContent portal>
      <ComboboxList>{children}</ComboboxList>
    </MenuContent>
  </Menu>
)

const TransformActionGroup = () => {
  const actions = useNodeTransformActions()
  const { canReset, handleResetFormatting, label, Icon } =
    useResetAllFormatting({
      hideWhenUnavailable: true,
      preserveMarks: ["inlineThread"],
    })

  if (!actions && !canReset) return null

  return (
    <>
      {actions && (
        <SubMenuTrigger icon={Repeat2Icon} label="Turn Into">
          <MenuGroup>
            <MenuGroupLabel>Turn into</MenuGroupLabel>
              {actions.map((action) => (
                action.items ? (
                  <SubMenuTrigger key={action.label} icon={action.icon} label={action.label}>
                    <MenuGroup>
                      <MenuGroupLabel>Callout</MenuGroupLabel>
                      {action.items.map((item) => (
                        <BaseMenuItem key={`${action.label}-${item.label}`} {...item} />
                      ))}
                    </MenuGroup>
                  </SubMenuTrigger>
                ) : (
                  <BaseMenuItem key={action.label} {...action} />
                )
              ))}
          </MenuGroup>
        </SubMenuTrigger>
      )}
      {canReset && (
        <BaseMenuItem
          icon={Icon}
          label={label}
          disabled={!canReset}
          onClick={handleResetFormatting} />
      )}
    </>
  );
}

const TableFitToWidth = () => {
  const { canFitToWidth, handleFitToWidth, label, Icon } = useTableFitToWidth({
    hideWhenUnavailable: true,
  })
  const clearAllContents = useTableClearRowColumnContent({ resetAttrs: true })

  return (
    <>
      {canFitToWidth && (
        <BaseMenuItem
          icon={Icon}
          label={label}
          disabled={!canFitToWidth}
          onClick={handleFitToWidth} />
      )}
      {clearAllContents.canClearRowColumnContent && (
        <BaseMenuItem
          icon={clearAllContents.Icon}
          label={"Clear all contents"}
          disabled={!clearAllContents.canClearRowColumnContent}
          onClick={clearAllContents.handleClear} />
      )}
    </>
  );
}

const TocShowTitle = () => {
  const { canToggle, handleToggle, label, Icon } = useTocShowTitle({
    hideWhenUnavailable: true,
  })

  if (!canToggle) return null

  return (<BaseMenuItem icon={Icon} label={label} disabled={!canToggle} onClick={handleToggle} />);
}

const ImageActionGroup = () => {
  const { canDownload, handleDownload, label, Icon } = useImageDownload({
    hideWhenUnavailable: true,
  })

  if (!canDownload) return null

  return (
    <BaseMenuItem
      icon={Icon}
      label={label}
      disabled={!canDownload}
      onClick={handleDownload} />
  );
}

const MediaActionGroup = ({
  editor,
  node,
  nodePos,
}) => {
  const mediaValues = getMediaFormValues(node)

  if (!editor || !mediaValues || !isMediaNodeType(node?.type?.name)) {
    return null
  }

  return (
    <Menu
      placement="right"
      trigger={
        <MenuItem
          render={
            <MenuButton
              render={
                <Button variant="ghost">
                  <ExternalLinkIcon className="tiptap-button-icon" />
                  <span className="tiptap-button-text">Edit media</span>
                  <Spacer />
                  <ChevronRightIcon className="tiptap-button-icon" />
                </Button>
              } />
          }
          preventClose />
      }>
      <MenuContent portal>
        <Card
          className="tiptap-slash-card tiptap-slash-card--media"
          onPointerDown={(event) => event.stopPropagation()}>
          <CardBody className="tiptap-slash-card-body tiptap-slash-card-body--media">
            <CardItemGroup>
              <CardGroupLabel>Media</CardGroupLabel>
              <MediaForm
                initialSource={mediaValues.source}
                initialWidth={mediaValues.width}
                initialHeight={mediaValues.height}
                initialEmbed={mediaValues.embedHtml}
                initialMode={mediaValues.embedHtml ? "embed" : "general"}
                submitLabel="Save Media"
                invalidMessage="Enter a media source"
                onSubmit={(values) => updateMediaNode(editor, nodePos, node, values)} />
            </CardItemGroup>
          </CardBody>
        </Card>
      </MenuContent>
    </Menu>
  )
}

const CoreActionGroup = () => {
  const {
    handleDuplicate,
    canDuplicate,
    label,
    Icon: DuplicateIcon,
  } = useDuplicate()
  const {
    handleCopyToClipboard,
    canCopyToClipboard,
    label: copyLabel,
    Icon: CopyIcon,
  } = useCopyToClipboard()
  const {
    handleCopyAnchorLink,
    canCopyAnchorLink,
    label: copyAnchorLinkLabel,
    Icon: CopyAnchorLinkIcon,
  } = useCopyAnchorLink()

  return (
    <>
      <Separator orientation="horizontal" />
      <MenuGroup>
        <BaseMenuItem
          icon={DuplicateIcon}
          label={label}
          onClick={handleDuplicate}
          disabled={!canDuplicate}
          shortcutBadge={<DuplicateShortcutBadge />} />
        <BaseMenuItem
          icon={CopyIcon}
          label={copyLabel}
          onClick={handleCopyToClipboard}
          disabled={!canCopyToClipboard}
          shortcutBadge={<CopyToClipboardShortcutBadge />} />
        <BaseMenuItem
          icon={CopyAnchorLinkIcon}
          label={copyAnchorLinkLabel}
          onClick={handleCopyAnchorLink}
          disabled={!canCopyAnchorLink}
          shortcutBadge={<CopyAnchorLinkShortcutBadge />} />
      </MenuGroup>
      <Separator orientation="horizontal" />
    </>
  );
}

const AIActionGroup = () => {
  const { handleAiAsk, canAiAsk, Icon: AiAskIcon } = useAiAsk()

  if (!canAiAsk) return null

  return (
    <>
      <MenuGroup>
        {canAiAsk && (
          <BaseMenuItem
            icon={AiAskIcon}
            label="Ask AI"
            onClick={handleAiAsk}
            shortcutBadge={<AskAiShortcutBadge />} />
        )}
      </MenuGroup>
      <Separator orientation="horizontal" />
    </>
  );
}

const DeleteActionGroup = () => {
  const { handleDeleteNode, canDeleteNode, label, Icon } = useDeleteNode()

  return (
    <MenuGroup>
      <BaseMenuItem
        icon={Icon}
        label={label}
        onClick={handleDeleteNode}
        disabled={!canDeleteNode}
        shortcutBadge={<DeleteNodeShortcutBadge />} />
    </MenuGroup>
  );
}

export const DragContextMenu = ({
  editor: providedEditor,
  withSlashCommandTrigger = true,
  mobileBreakpoint = 768,
  ...props
}) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { aiGenerationActive, isDragging } = useUiEditorState(editor)
  const isMobile = useIsBreakpoint("max", mobileBreakpoint)
  const [open, setOpen] = useState(false)
  const [node, setNode] = useState(null)
  const [nodePos, setNodePos] = useState(-1)
  const [dragAutoScrollActive, setDragAutoScrollActive] = useState(false)
  const dragAutoScrollActiveRef = useRef(false)

  const handleNodeChange = useCallback((data) => {
    if (data.node) setNode(data.node)
    setNodePos(data.pos)
  }, [])

  useEffect(() => {
    if (!editor) return
    editor.commands.setLockDragHandle(open)
    editor.commands.setMeta("lockDragHandle", open)
  }, [editor, open])

  const mainAxisOffset = 16

  const dynamicPositions = useMemo(() => {
    return {
      middleware: [
        offset((props) => {
          const { rects } = props
          const nodeHeight = rects.reference.height
          const dragHandleHeight = rects.floating.height

          const crossAxis = nodeHeight / 2 - dragHandleHeight / 2

          return {
            mainAxis: mainAxisOffset,
            // if height is more than 40px, then it's likely a block node
            crossAxis: nodeHeight > 40 ? 0 : crossAxis,
          }
        }),
      ],
    };
  }, [])

  const startElementDrag = useCallback(() => {
    if (!editor) return
    dragAutoScrollActiveRef.current = true
    editor.commands.setIsDragging(true)
    setDragAutoScrollActive(true)
  }, [editor])

  const stopElementDrag = useCallback(() => {
    if (!editor) return
    const wasDragging = dragAutoScrollActiveRef.current
    dragAutoScrollActiveRef.current = false
    editor.commands.setIsDragging(false)
    setDragAutoScrollActive(false)

    if (!wasDragging) return

    setTimeout(() => {
      editor.view.dom.blur()
      editor.view.focus()
    }, 0)
  }, [editor])

  useEffect(() => {
    if (!editor) return

    const handleDragStart = (event) => {
      if (
        isDragHandleEventTarget(event.target) ||
        isImageDragHandleEventTarget(event.target)
      ) {
        startElementDrag()
      }
    }

    document.addEventListener("dragstart", handleDragStart, true)
    document.addEventListener("dragend", stopElementDrag, true)
    document.addEventListener("drop", stopElementDrag, true)

    return () => {
      document.removeEventListener("dragstart", handleDragStart, true)
      document.removeEventListener("dragend", stopElementDrag, true)
      document.removeEventListener("drop", stopElementDrag, true)
    }
  }, [editor, startElementDrag, stopElementDrag])

  useDragAutoScroll(editor, dragAutoScrollActive)

  if (!editor) return null

  const nodeName = getNodeDisplayName(editor)

  return (
    <div
      style={
        {
          "--drag-handle-main-axis-offset": `${mainAxisOffset}px`
        }
      }>
      <DragHandle
        editor={editor}
        onNodeChange={handleNodeChange}
        computePositionConfig={dynamicPositions}
        nested={rootLevelOrImageDragTargets}
        onElementDragStart={startElementDrag}
        onElementDragEnd={stopElementDrag}
        {...props}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            ...(aiGenerationActive || isMobile || isTextSelectionValid(editor)
              ? { opacity: 0, pointerEvents: "none" }
              : {}),
            ...(isDragging ? { opacity: 0 } : {}),
          }}>
          {withSlashCommandTrigger && (
            <SlashCommandTriggerButton node={node} nodePos={nodePos} data-weight="small" />
          )}

          <Menu
            open={open}
            onOpenChange={setOpen}
            placement="left"
            trigger={
              <MenuButton
                render={
                  <Button
                    variant="ghost"
                    tabIndex={-1}
                    tooltip={
                      <>
                        <div>Click for options</div>
                        <div>Hold for drag</div>
                      </>
                    }
                    data-weight="small"
                    style={{
                      cursor: "grab",
                      ...(open ? { pointerEvents: "none" } : {}),
                    }}
                    onMouseDown={() =>
                      selectNodeAndHideFloating(editor, nodePos)
                    }>
                    <GripVerticalIcon className="tiptap-button-icon" />
                  </Button>
                } />
            }>
            <MenuContent
              autoFocusOnHide={false}
              portal>
              <Combobox style={SR_ONLY} />
              <ComboboxList style={{ minWidth: "15rem" }}>
                <MenuGroupLabel>{nodeName}</MenuGroupLabel>

                <MenuGroup>
                  <TocShowTitle />
                  <ColorMenu />
                  <TableAlignMenu />
                  <TableFitToWidth />
                  <TransformActionGroup />
                  <ImageActionGroup />
                  <MediaActionGroup editor={editor} node={node} nodePos={nodePos} />
                </MenuGroup>

                <CoreActionGroup />

                <AIActionGroup />

                <DeleteActionGroup />
              </ComboboxList>
            </MenuContent>
          </Menu>
        </div>
      </DragHandle>
    </div>
  );
}
